import { Link, useLocation } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { isSidebarNavActive } from '@/lib/sidebar-nav-active'
import styles from './SidebarItem.module.css'

type SidebarItemProps = {
  to: string
  label: string
  icon?: string
  exact?: boolean
  sectionPrefix?: string
  description?: string
  /** Destaque de alerta quando há pendência de configuração (ex.: gateway sem ID salvo). */
  alert?: boolean
}

function LinkContent({
  icon,
  label,
  description,
  alert,
}: {
  icon?: string
  label: string
  description?: string
  alert?: boolean
}) {
  return (
    <>
      {icon && (
        <span className={styles.icon} aria-hidden>
          <Icon icon={icon} width="18" height="18" />
        </span>
      )}
      <span className={styles.content}>
        <span className={styles.label}>{label}</span>
        {description && (
          <span className={styles.description}>{description}</span>
        )}
      </span>
      {alert && <span className={styles.alertDot} aria-hidden />}
    </>
  )
}

function resolveLinkClassName(isActive: boolean, alert: boolean): string {
  if (isActive && alert) {
    return `${styles.link} ${styles.linkActive} ${styles.linkAlert} ${styles.linkAlertActive}`
  }
  if (isActive) {
    return `${styles.link} ${styles.linkActive}`
  }
  if (alert) {
    return `${styles.link} ${styles.linkAlert}`
  }
  return styles.link
}

export function SidebarItem({
  to,
  label,
  icon,
  exact,
  sectionPrefix,
  description,
  alert = false,
}: SidebarItemProps) {
  const location = useLocation()
  const isHashLink = to === '#' || to.startsWith('#')
  const isActive = isSidebarNavActive(location.pathname, { to, exact, sectionPrefix })
  const linkClassName = resolveLinkClassName(isActive, alert)
  const accessibleLabel = alert ? `${label}, configuração pendente` : label

  if (isHashLink) {
    return (
      <Link to={to} className={styles.link} aria-label={accessibleLabel}>
        <LinkContent icon={icon} label={label} description={description} alert={alert} />
      </Link>
    )
  }

  return (
    <Link
      to={to}
      aria-current={isActive ? 'page' : undefined}
      aria-label={accessibleLabel}
      className={linkClassName}
    >
      <LinkContent icon={icon} label={label} description={description} alert={alert} />
    </Link>
  )
}
